import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Trophy, Coins, Lock, ShoppingBag, Sparkles, Award, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BadgeTile } from "@/components/BadgeStrip";
import "@/styles/badges.css";

/**
 * /badges — full directory + my collection + shop. Tabs: All / Owned /
 * Earnable / Shop. Owned badges can be equipped/unequipped here.
 *
 * Equip flow: any number can be equipped (server stores up to 25). The
 * UI displays max 5 inline next to a username + "+N" overflow.
 */
export default function BadgesPage() {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [mine, setMine] = useState({ owned: [], equipped: [], equipped_skus: [] });
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");

  const reload = async () => {
    setBusy(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/badges"),
        user ? api.get("/badges/me") : Promise.resolve({ data: { owned: [], equipped: [], equipped_skus: [] } }),
      ]);
      setCatalog(a.data?.items || []);
      setMine({
        owned: b.data?.owned || [],
        equipped: b.data?.equipped || [],
        equipped_skus: b.data?.equipped_skus || [],
      });
    } catch {
      setCatalog([]);
      setMine({ owned: [], equipped: [], equipped_skus: [] });
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user?._id]);

  const ownedSkus = useMemo(() => new Set(mine.owned.map((b) => b.sku)), [mine.owned]);

  const buy = async (sku) => {
    if (!user) return toast.error("Sign in to buy badges");
    try {
      const { data } = await api.post(`/badges/${sku}/buy`);
      toast.success(`Badge unlocked! Balance: ${data.balance}c`);
      reload();
      if (refresh) refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purchase failed");
    }
  };

  const toggleEquip = async (sku) => {
    if (!user) return;
    const cur = new Set(mine.equipped_skus || []);
    if (cur.has(sku)) cur.delete(sku);
    else cur.add(sku);
    const next = (mine.equipped_skus || []).filter((s) => cur.has(s));
    if (cur.has(sku)) next.push(sku);
    try {
      await api.post("/badges/equip", { skus: next });
      toast.success("Equipped layout updated");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const filtered = useMemo(() => {
    let list = catalog;
    if (tab === "owned") list = catalog.filter((b) => ownedSkus.has(b.sku));
    if (tab === "earnable") list = catalog.filter((b) => b.source === "earned" && !ownedSkus.has(b.sku));
    if (tab === "shop") list = catalog.filter((b) => b.source === "purchased" && !ownedSkus.has(b.sku));
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      list = list.filter((b) => b.name.toLowerCase().includes(qq) || (b.lore || "").toLowerCase().includes(qq));
    }
    return list;
  }, [catalog, tab, ownedSkus, q]);

  if (busy && catalog.length === 0) {
    return <div className="py-20 text-center"><Loader2 size={28} className="inline animate-spin text-[#D7A62A]" /></div>;
  }

  return (
    <div className="py-10 space-y-8" data-testid="badges-page">
      <header className="max-w-3xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>show your colors</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2 leading-[0.9]">
          The
          <span style={{ color: "#76B82A" }}> Badge </span>
          Case.
        </h1>
        <p className="mt-3 text-base text-[#F5F2E8]/75 max-w-xl mx-auto">
          Earn 'em. Buy 'em. Show 'em. Equip up to 5 next to your username — the rest live on your profile.
        </p>
      </header>

      {user && (
        <div className="max-w-3xl mx-auto loot-card-flat p-4" data-testid="my-badge-case">
          <div className="overline mb-3">Your equipped badges</div>
          {mine.equipped.length === 0 ? (
            <div className="font-mono text-xs text-[#F5F2E8]/55 italic">
              No badges equipped yet. Tap any owned badge below to add it to your loadout.
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {mine.equipped.map((b) => (
                <button
                  key={b.sku}
                  onClick={() => toggleEquip(b.sku)}
                  className="flex items-center gap-2 border p-1.5 hover:border-[#E53935] transition-colors"
                  style={{ borderColor: "rgba(245,242,232,0.15)" }}
                  data-testid={`equipped-badge-${b.sku}`}
                  title="Click to unequip"
                >
                  <BadgeTile badge={b} size={32} showTooltip={false} />
                  <span className="font-display uppercase text-xs">{b.name}</span>
                </button>
              ))}
            </div>
          )}
          <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-2">
            Click an equipped badge to remove it · {mine.owned.length} owned total
          </p>
        </div>
      )}

      <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-2 border-b border-[#F5F2E8]/10 pb-2">
        {[
          ["all", "All", catalog.length],
          ["owned", "Mine", mine.owned.length],
          ["earnable", "Earnable", catalog.filter((b) => b.source === "earned" && !ownedSkus.has(b.sku)).length],
          ["shop", "Shop", catalog.filter((b) => b.source === "purchased" && !ownedSkus.has(b.sku)).length],
        ].map(([id, label, n]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`badges-tab-${id}`}
            className="text-xs font-display uppercase tracking-widest px-3 py-1.5 border"
            style={{
              borderColor: tab === id ? "#76B82A" : "rgba(245,242,232,0.15)",
              color: tab === id ? "#76B82A" : "#F5F2E8",
              background: tab === id ? "rgba(118,184,42,0.08)" : "transparent",
            }}
          >
            {label} <span className="text-[#F5F2E8]/55 ml-1">({n})</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <Search size={12} className="text-[#F5F2E8]/45" />
          <input
            className="loot-input"
            style={{ width: 180 }}
            placeholder="Filter…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="badges-search"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
        {filtered.map((b) => {
          const owned = ownedSkus.has(b.sku);
          const equipped = (mine.equipped_skus || []).includes(b.sku);
          return (
            <div key={b.sku} className="loot-card-flat p-4 space-y-2" data-testid={`badge-card-${b.sku}`}>
              <div className="flex items-start gap-3">
                <BadgeTile badge={b} size={56} locked={!owned} showTooltip={false} />
                <div className="flex-1">
                  <div className="font-display uppercase text-sm">{b.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 mt-0.5">
                    <span style={{ color: rarityColor(b.rarity) }}>{b.rarity}</span> · {b.source}
                  </div>
                  {b.lore && <p className="text-xs text-[#F5F2E8]/75 mt-1">{b.lore}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {owned && (
                  <button
                    onClick={() => toggleEquip(b.sku)}
                    className={equipped ? "btn-ghost text-xs" : "btn-loot text-xs"}
                    style={equipped ? { color: "#76B82A", borderColor: "#76B82A" } : {}}
                    data-testid={`badge-equip-${b.sku}`}
                  >
                    {equipped ? <><Check size={12} className="inline mr-1" /> Equipped</> : "Equip"}
                  </button>
                )}
                {!owned && b.source === "purchased" && (
                  <button
                    onClick={() => buy(b.sku)}
                    className="btn-loot text-xs inline-flex items-center gap-1"
                    data-testid={`badge-buy-${b.sku}`}
                  >
                    <Coins size={12} /> Buy {b.cost_coins}c
                  </button>
                )}
                {!owned && b.source === "earned" && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 inline-flex items-center gap-1">
                    <Lock size={10} /> Earn it: {triggerHint(b)}
                  </span>
                )}
                {!owned && b.source === "awarded" && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 inline-flex items-center gap-1">
                    <Award size={10} /> Awarded by admin only
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="sm:col-span-2 text-center py-8 font-mono text-xs text-[#F5F2E8]/55">
            No badges in this filter.
          </div>
        )}
      </div>
    </div>
  );
}

function rarityColor(r) {
  return {
    common: "#9aa0a6", uncommon: "#76B82A", rare: "#4ea4ff",
    epic: "#b760ff", legendary: "#D7A62A",
  }[r] || "#9aa0a6";
}

function triggerHint(b) {
  const t = b.trigger_event;
  const n = b.trigger_threshold;
  switch (t) {
    case "stash_streak": return `Day-${n} Stash streak`;
    case "first_drop_found": return "Find your first drop";
    case "first_vault_crack": return "Crack a Daily Vault";
    case "vault_wins": return `Win ${n || 1} vaults`;
    case "tank_donor": return "Donate to the Tank";
    case "big_donor": return `Donate $${((n||0)/100).toFixed(0)}+ to the Tank`;
    case "featured_holler": return "Get featured on The Holler";
    case "first_auction_win": return "Win an auction";
    case "stash_total_wins": return `Win ${n || 1} Stash prizes`;
    default: return "Coming soon";
  }
}

// =====================================================================
// /u/:username/badges — public profile-style badge case
// =====================================================================
export function PublicUserBadges() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get(`/badges/by-username/${encodeURIComponent(username)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ username, owned: [], equipped: [] }); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [username]);

  if (busy) return <div className="py-20 text-center"><Loader2 size={28} className="inline animate-spin text-[#D7A62A]" /></div>;

  return (
    <div className="py-10 space-y-6" data-testid="public-user-badges">
      <header className="max-w-3xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>hunter profile</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mt-2 leading-[0.9]">
          @{data?.username || username}
        </h1>
        <p className="mt-2 font-mono text-xs text-[#F5F2E8]/55">
          {data?.owned_count || 0} badges owned
        </p>
      </header>

      <div className="max-w-3xl mx-auto loot-card-flat p-4">
        <div className="overline mb-2">Equipped</div>
        {(data?.equipped || []).length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/55 italic">No badges equipped.</div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {(data?.equipped || []).map((b) => (
              <div key={b.sku} className="flex items-center gap-2 border p-1.5" style={{ borderColor: "rgba(245,242,232,0.15)" }}>
                <BadgeTile badge={b} size={32} showTooltip={false} />
                <span className="font-display uppercase text-xs">{b.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto loot-card-flat p-4">
        <div className="overline mb-2">All owned</div>
        {(data?.owned || []).length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/55 italic">No badges owned yet.</div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {(data?.owned || []).map((b) => (
              <div key={b.sku} className="text-center">
                <BadgeTile badge={b} size={48} showTooltip={false} />
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#F5F2E8]/55 mt-1 truncate">{b.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
